import React, {useEffect, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {InputLabel, Typography} from 'onboarding-components';
import clsx from 'clsx';
import {create, InstanceProps} from 'react-modal-promise';

import {useFeatureRollout} from 'common/hooks/useFeatureRollout';
import {BubbleOptions, Buttons} from 'common/onboarding/components/user-attributes';
import {ModalReact, ModalType, VerticalAlignment} from 'common/components/modal';
import {queryClient} from 'common/react-query/query-client';
import {OnboardingQuestionnaireData} from 'common/types/schema4api/webapp/user/types';
import {TranslationKey} from 'common/i18n';
import {useTranslation} from 'common/i18n/hooks';
import {apiClient} from 'common/util/api-client';
import {FeatureRolloutName} from 'common/report/util/feature-sampling';
import {useThemes} from 'common/contexts/react/themes-context';
import {StackableComponent} from 'common/components/base/stackable';
import {useLocalStorageManager} from 'common/contexts/react/storage-manager-context';
import {getDownloadMode, isCheckoutMode, isPublicMode} from 'common/report/util';
import {DownloadMode} from 'common/report/constants';
import {getUserFlags, incrementUserFlag} from 'common/report/queries/app-api/user-flags';
import {UserFlag} from 'common/report/constants';
import {getUserId} from 'common/widgets/content-sidenav/util';

import {SURVEY_DATA} from '../segmented-onboarding/constants';
import {QuestionType} from '../segmented-onboarding/types';
import {TextInput} from '../immersive-onboarding/components/text-input';
import {useSmallScreen} from '../utils';
import styles from './index.module.styl';
import textInputStyleOverride from './text-input.inline.styl';
import textInputLightStyleOverride from './text-input-light.inline.styl';

enum PageType {
  Name = `name`,
  Role = `role`,
  Work = `work`,
  Thanks = `thanks`,
}

const enum OnboardingQuestionnaireTrackingEvents {
  Started = `[Onboarding Questionnaire] Started Questionnaire`,
  Skipped = `[Onboarding Questionnaire] Skipped Questionnaire`,
  SelectedOption = `[Onboarding Questionnaire] Selected Option`,
  SubmitBtnClicked = `[Onboarding Questionnaire] Questionnaire Submitted`,
  NextBtnClicked = `[Onboarding Questionnaire] Next Button Clicked`,
}

function getLocalStorageKey(type: string) {
  return `$mp_onboarding_questionnaire_${type}`;
}

/**
 * @deprecated
 * This function is used to get the old localStorage key for the onboarding questionnaire so we don't suddenly
 * lose track of the number of skips or when the user last dismissed the modal.
 * This function should be removed once we're confident that the new key is being used.
 */
function getOldNonGdprAllowedLocalStorageKey(type: string) {
  const userId = getUserId();
  return `onboarding-questionnaire:${userId}:${type}`;
}

type NamePageProps = {
  data: OnboardingQuestionnaireData;
  setData: (value: OnboardingQuestionnaireData) => void;
  hasPreviousResponse: boolean;
};
function NamePage({data, setData, hasPreviousResponse}: NamePageProps) {
  const {t} = useTranslation();
  return (
    <>
      <Title>{t(`signup.name.title`)}</Title>
      <Subtitle>{t(hasPreviousResponse ? `signup.name.short-subtitle` : `signup.name.subtitle`)}</Subtitle>
      <TextInputContainer
        className={styles.firstInput}
        label='signup.name.first-name'
        autoFocus
        defaultValue={data.firstname}
        placeholder='signup.name.first-name-placeholder'
        onChange={(firstname) => setData({...data, firstname})}
      />
      <TextInputContainer
        className={styles.lastInput}
        label='signup.name.last-name'
        defaultValue={data.lastname}
        placeholder='signup.name.last-name-placeholder'
        onChange={(lastname) => setData({...data, lastname})}
      />
    </>
  );
}

type WorkPageProps = {
  data: OnboardingQuestionnaireData;
  setData: (value: OnboardingQuestionnaireData) => void;
  featureGroup: string;
};
function WorkPage({data, setData, featureGroup}: WorkPageProps) {
  const {t} = useTranslation();
  return (
    <>
      <Title>{t(`signup.work.title`)}</Title>
      <Subtitle>{t(`signup.work.subtitle`)}</Subtitle>
      <BubbleOptions
        className={styles.bubbleOption}
        options={`options` in SURVEY_DATA[QuestionType.Work] ? SURVEY_DATA[QuestionType.Work].options : []}
        selected={data.work ? new Set([data.work]) : new Set()}
        onSelect={(work) => {
          if (work !== `Other`) {
            delete data[`work_other`];
          }
          setData({...data, work});

          mixpanel.track(OnboardingQuestionnaireTrackingEvents.SelectedOption, {
            featureGroup,
            type: PageType.Work,
            response: work,
            action: data.work === work ? `deselect` : `select`,
            version: `Onboarding Questionnaire`,
          });
        }}
        otherPlaceholder={({t}) => t(`signup.work.placeholder`)}
        otherInput={() => (
          <TextInputContainer
            className={clsx(styles.defaultInput, {
              [styles.showInput]: data.work === `Other`,
            })}
            placeholder='signup.work.placeholder'
            onChange={(work) => setData({...data, work_other: work})}
          />
        )}
      />
    </>
  );
}

type RolePageProps = {
  data: OnboardingQuestionnaireData;
  setData: (value: OnboardingQuestionnaireData) => void;
};
function RolePage({data, setData}: RolePageProps) {
  const {t} = useTranslation();
  return (
    <>
      <Title>{t(`signup.role.title`)}</Title>
      <Subtitle>{t(`signup.role.subtitle`)}</Subtitle>
      <BubbleOptions
        className={styles.bubbleOption}
        options={`options` in SURVEY_DATA[QuestionType.Role] ? SURVEY_DATA[QuestionType.Role].options : []}
        selected={data.role ? new Set([data.role]) : new Set()}
        onSelect={(role) => setData({...data, role})}
      />
    </>
  );
}

function Title({children}: {children: React.ReactNode}) {
  const smallScreen = useSmallScreen();

  return (
    <Typography className={styles.title} variant={smallScreen ? `headingXs` : `headingSm`}>
      {children}
    </Typography>
  );
}

function Subtitle({children}: {children: React.ReactNode}) {
  return (
    <Typography className={styles.subTitle} variant='bodyMd'>
      {children}
    </Typography>
  );
}

type TextInputContainerProps = {
  autoFocus?: boolean;
  className?: string;
  defaultValue?: string;
  label?: TranslationKey;
  placeholder: TranslationKey;
  onChange: (value: string) => void;
};
function TextInputContainer({
  autoFocus,
  className,
  defaultValue,
  label,
  placeholder,
  onChange,
}: TextInputContainerProps) {
  const {t} = useTranslation();
  const isDarkMode = useThemes().isDarkMode();
  const extraCss = isDarkMode ? textInputStyleOverride : `${textInputStyleOverride}\n${textInputLightStyleOverride}`;

  return (
    <div className={clsx(styles.textInput, styles.textInput, className)}>
      {label && <InputLabel className={styles.textInputLabel} name={t(label)} />}
      <TextInput
        extraCss={extraCss}
        autoFocus={autoFocus}
        defaultValue={defaultValue}
        placeholder={t(placeholder)}
        validator={(value) => value.trim().length > 0}
        onChange={onChange}
      />
    </div>
  );
}

type PageProps = {
  isOpen: boolean;
  children?: React.ReactNode;
  onNext?: () => void;
  onSubmit?: () => void;
  onReject: () => void;
  pageType: PageType;
  isNextEnabled: boolean;
  featureGroup: string;
  closeable: boolean;
  onFinish?: () => void;
  later?: boolean;
  hasPreviousResponse?: boolean;
};
function Page({
  children,
  onNext,
  pageType,
  isNextEnabled,
  featureGroup,
  isOpen,
  closeable,
  onReject,
  onFinish,
  later,
  hasPreviousResponse,
}: PageProps) {
  const isDarkMode = useThemes().isDarkMode();

  return (
    <ModalReact
      closeButtonPadded
      open={isOpen}
      slidingTransition
      modalType={ModalType.Modal}
      closeable={closeable}
      growToContent
      verticalAlignment={VerticalAlignment.Center}
      onStackableClosed={onReject}
    >
      <div className={clsx(styles.page, {[styles.darkMode]: isDarkMode, [styles.lightMode]: !isDarkMode})}>
        {children}
        <Buttons
          isNextEnabled={!!isNextEnabled}
          onNext={
            onNext &&
            (() => {
              mixpanel.track(OnboardingQuestionnaireTrackingEvents.NextBtnClicked, {
                featureGroup,
                type: pageType,
                version: `Onboarding Questionnaire`,
              });
              onNext();
            })
          }
          onFinish={onFinish}
          onLater={later ? onReject : undefined}
          darkMode={isDarkMode}
          hasPreviousResponse={hasPreviousResponse}
        />
      </div>
    </ModalReact>
  );
}

type ModalProps = {
  closeable: boolean;
  data: OnboardingQuestionnaireData;
  featureGroup: string;
  hasPreviousResponse: boolean;
  setIsSubmitted: (value: boolean) => void;
  onFinish: (data: OnboardingQuestionnaireData) => void;
} & InstanceProps<OnboardingQuestionnaireData>;

const openNameModal = create(
  ({closeable, data, featureGroup, hasPreviousResponse, setIsSubmitted, onResolve, onFinish, ...rest}: ModalProps) => {
    const [nextData, setData] = useState(data);

    return (
      <Page
        pageType={PageType.Name}
        closeable={false} // hide the x on the first page
        onNext={() => {
          if (hasPreviousResponse) {
            apiClient.post(`/onboarding-questionnaire/`, nextData);
            setIsSubmitted(true);
            mixpanel.track(OnboardingQuestionnaireTrackingEvents.SubmitBtnClicked, {
              featureGroup,
              version: `Onboarding Questionnaire`,
            });
          }
          onResolve(nextData);
        }}
        isNextEnabled={!!nextData.firstname?.trim() && !!nextData.lastname?.trim()}
        featureGroup={featureGroup}
        later={closeable}
        hasPreviousResponse={hasPreviousResponse}
        {...rest}
      >
        <NamePage data={nextData} setData={setData} hasPreviousResponse={hasPreviousResponse} />
      </Page>
    );
  },
);

const openWorkModal = create(({data, featureGroup, hasPreviousResponse, onResolve, onFinish, ...rest}: ModalProps) => {
  const [nextData, setData] = useState(data);

  return (
    <Page
      pageType={PageType.Work}
      onNext={() => onResolve(nextData)}
      isNextEnabled={
        (nextData.work === `Other` && !!nextData.work_other?.trim()) || (nextData.work !== `Other` && !!nextData.work)
      }
      featureGroup={featureGroup}
      {...rest}
    >
      <WorkPage data={nextData} setData={setData} featureGroup={featureGroup} />
    </Page>
  );
});

const openRoleModal = create(
  ({data, featureGroup, setIsSubmitted, hasPreviousResponse, onResolve, onFinish, ...rest}: ModalProps) => {
    const [nextData, setData] = useState(data);
    return (
      <Page
        pageType={PageType.Role}
        onFinish={() => {
          onFinish(nextData);
          onResolve(nextData);
        }}
        isNextEnabled={!!nextData.role}
        featureGroup={featureGroup}
        {...rest}
      >
        <RolePage data={nextData} setData={setData} />
      </Page>
    );
  },
);

const openThanksModal = create(
  ({closeable: _, data, featureGroup, hasPreviousResponse, onResolve, onFinish, ...rest}: ModalProps) => {
    const {t} = useTranslation();

    useEffect(() => {
      const timer = setTimeout(() => {
        onResolve(data);
      }, 3000);

      return () => clearTimeout(timer);
    }, [data, onResolve]);

    return (
      <Page pageType={PageType.Thanks} featureGroup={featureGroup} isNextEnabled={false} closeable {...rest}>
        <Title>{t(`signup.thanks.title`, {firstName: data.firstname})}</Title>
        <Subtitle>{t(`signup.thanks.subtitle`)}</Subtitle>
      </Page>
    );
  },
);

const OpenableOnboardingQuestionnaire = () => {
  const localStorageManager = useLocalStorageManager();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [localLastDismissed, setLocalLastDismissed] = useState<string | undefined>(undefined);

  const isFFEnabled = useFeatureRollout(FeatureRolloutName.OnboardingQuestionnaire);
  const featureGroup = isFFEnabled ? `treatment` : `control`;

  // see if there was a previous questionnaire response
  const {
    data: savedResponse,
    isSuccess,
    isFetching,
  } = useQuery({
    queryKey: [`onboarding_questionnaire_data`],
    queryFn: async () => {
      return apiClient.get<OnboardingQuestionnaireData>(`/onboarding-questionnaire/`);
    },
    // we don't want to refetch anytime, since this data isn't going to change
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const {data: userFlags, isFetching: isLoadingUserFlags} = useQuery({
    queryKey: [`userFlags`],
    queryFn: getUserFlags,
  });

  const hasShownTooManyTimes = userFlags?.[UserFlag.OnboardingQuestionnaireShownCount] ?? 0 > 8;

  // if another modal is active, we'll show this next time they come to the page
  const otherModalActive = StackableComponent.getCurrentStackingContextDepth() > 0;

  // if we're showing a tour model, let's not show the onboarding questionnaire for a day
  if (otherModalActive && !localLastDismissed) {
    localStorageManager.setItem(getLocalStorageKey(`dismissed`), Date.now().toString());
    setLocalLastDismissed(Date.now().toString());
  }

  // only pop the modal once a day if they dismiss it
  const lastDismissed =
    localStorageManager.getItem(getLocalStorageKey(`dismissed`)) ||
    localStorageManager.getItem(getOldNonGdprAllowedLocalStorageKey(`dismissed`)) ||
    localLastDismissed;
  const dismissDate = lastDismissed ? new Date(parseInt(lastDismissed, 10)) : undefined;
  const dismissedLessThan24HoursAgo = dismissDate && Date.now() - dismissDate.getTime() < 24 * 60 * 60 * 1000;

  const needsData =
    savedResponse &&
    (!savedResponse.firstname || !savedResponse.lastname || !savedResponse.role || !savedResponse.work);

  const shouldHideModal =
    !isFFEnabled ||
    isFetching ||
    !isSuccess ||
    !needsData ||
    savedResponse === undefined ||
    otherModalActive ||
    dismissedLessThan24HoursAgo ||
    getDownloadMode() !== DownloadMode.None ||
    isPublicMode() ||
    isCheckoutMode() || // never show the questionnaire modal if a user is checking out
    isSubmitted;

  useEffect(() => {
    // if the modal shouldn't be shown or the feature flag isn't on, there's no need to show the modal
    // also only show it to users who haven't seen it too many times
    if (!shouldHideModal && isFFEnabled && !isLoadingUserFlags && !hasShownTooManyTimes) {
      const storedSkips =
        localStorageManager.getItem(getLocalStorageKey(`skips`)) ||
        localStorageManager.getItem(getOldNonGdprAllowedLocalStorageKey(`skips`));
      const skips = storedSkips ? parseInt(storedSkips, 10) : 0;
      // this will help us to understand why we're showing the modal
      mixpanel.track(OnboardingQuestionnaireTrackingEvents.Started, {
        featureGroup,
        version: `Onboarding Questionnaire`,
        needsFirst: !savedResponse.firstname,
        needsLast: !savedResponse.lastname,
        needsRole: !savedResponse.role,
        needsWork: !savedResponse.work,
        storedSkips,
        hasAlreadySubmitted: isSubmitted,
        dismissedLessThan24HoursAgo,
        dismissDate,
      });

      // increment the user flag for OnboardingQuestionnaireShownCount so we can stop showing to stubborn users
      // who refuse to fill out the questionnaire
      incrementUserFlag(UserFlag.OnboardingQuestionnaireShownCount);

      // if a userId is higher than a particular cutoff, we decide they're a new user and we don't allow
      // closing the modal
      const userId = getUserId() || 0;
      const isNewUser = userId > 5508368; // highest ID as of feb 24, 2025

      const onFinish = (nextData: OnboardingQuestionnaireData) => {
        apiClient.post(`/onboarding-questionnaire/`, nextData).then(() => {
          // once we've submitted the data, we force a refetch so that this component will not show again
          queryClient.invalidateQueries({
            queryKey: [`onboarding_questionnaire_data`],
            refetchType: `all`, // refetch both active and inactive queries
          });
        });
        setIsSubmitted(true);
        mixpanel.track(OnboardingQuestionnaireTrackingEvents.SubmitBtnClicked, {
          featureGroup,
          version: `Onboarding Questionnaire`,
        });
      };

      // we pull out only the values we care about for our submission, so that we don't
      // run into issues with legacy values that won't pass validation
      const previousData = {
        firstname: savedResponse.firstname,
        lastname: savedResponse.lastname,
        role: savedResponse.role,
        role_other: savedResponse.role_other,
        work: savedResponse.work,
        work_other: savedResponse.work_other,
      };

      const modalProps = {
        closeable: (skips < 3 && !isNewUser) || isSubmitted,
        data: previousData,
        featureGroup,
        hasPreviousResponse: !!previousData.role && !!previousData.work,
        setIsSubmitted,
        onFinish,
      };

      const onCloseModal = () => {
        if (!isSubmitted) {
          localStorageManager.setItem(getLocalStorageKey(`skips`), (skips + 1).toString());
          localStorageManager.setItem(getLocalStorageKey(`dismissed`), Date.now().toString());
          setLocalLastDismissed(Date.now().toString());
          mixpanel.track(OnboardingQuestionnaireTrackingEvents.Skipped, {
            featureGroup,
            version: `Onboarding Questionnaire`,
          });
        }
      };

      // in order to achieve the individual modal effect that design wanted, each page becomes a modal
      // that opens using react-modal-promise. Each modal takes the previous questionnaire data, updates
      // it with the properties on its page, and passes it to the next modal
      if (previousData.firstname?.trim() && previousData.lastname?.trim()) {
        // if we have their name, just skip the page
        openWorkModal(modalProps)
          .then((data) => openRoleModal({...modalProps, setIsSubmitted, data}))
          .then((data) => openThanksModal({...modalProps, data}))
          .catch(onCloseModal);
      } else {
        openNameModal(modalProps)
          .then((data) => {
            if (modalProps.hasPreviousResponse) {
              onFinish(data);
              return Promise.reject(new Error(`done`));
            }
            return openWorkModal({...modalProps, data});
          })
          .then((data) => openRoleModal({...modalProps, setIsSubmitted, data}))
          .then((data) => openThanksModal({...modalProps, data}))
          .catch(onCloseModal);
      }
    }
  }, [shouldHideModal, isLoadingUserFlags, hasShownTooManyTimes]);

  return null;
};

export const OnboardingQuestionnaire = () => {
  return <OpenableOnboardingQuestionnaire />;
};
